/**
 * Common React components, hooks, and UI logic for the signup management table.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCollection } from '@amzn/awsui-collection-hooks';
import Box from '@amzn/awsui-components-react/polaris/box';
import Select, { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import StatusIndicator from '@amzn/awsui-components-react/polaris/status-indicator';
import { Alert, Button, PropertyFilterProps, SpaceBetween } from '@amzn/awsui-components-react';
import Popover from '@amzn/awsui-components-react/polaris/popover';
import Icon from '@amzn/awsui-components-react/polaris/icon';
import { FilterProperty } from '../attendance/wizard/tablePropertyFilter';
import { ColumnMap } from '../../hooks/tableColumns';
import Link from '@amzn/awsui-components-react/polaris/link';
import { Event, Shift } from '@amzn/red-velvet-api';
import { useTimezone } from '../../hooks/timezone';
import { downloadCsv } from '../../utils/csvDownload';
import { publishKatalMetric } from '../katalAnalytics';
import {
  AmazonSignup,
  SignupStatus,
  buildSignupCsvRows,
  buildSignupCsvFilename,
  SIGNUP_CSV_HEADERS,
  getSignupBlockedReasons,
  SignupBlockedReason,
} from '../../utils/signupUtils';

export const INITIAL_SIGNUP_PAGE_SIZE = 100;

export function DismissConfirmationAlert({
  visible,
  onStay,
  onLeave,
}: {
  visible: boolean;
  onStay: () => void;
  onLeave: () => void;
}) {
  const { t } = useTranslation('signup');
  if (!visible) return null;

  return (
    <Box margin={{ bottom: 's' }}>
      <Alert
        type="warning"
        dismissible
        onDismiss={onStay}
        header={t('bulkSignupModal.dismissAlert.title')}
        action={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onStay}>{t('bulkSignupModal.dismissAlert.cancel')}</Button>
            <Button variant="primary" onClick={onLeave}>
              {t('bulkSignupModal.dismissAlert.confirm')}
            </Button>
          </SpaceBetween>
        }
      >
        {t('bulkSignupModal.dismissAlert.message')}
      </Alert>
    </Box>
  );
}

export function SignupBlockedPopover({
  blockedReasons,
  eventId,
  metricKey,
}: {
  blockedReasons: SignupBlockedReason[];
  eventId: string;
  metricKey?: string;
}) {
  const { t } = useTranslation('signup');
  if (blockedReasons.length === 0) return null;

  const showGivehubLink =
    !blockedReasons.includes(SignupBlockedReason.SHIFT_FULL) &&
    !blockedReasons.includes(SignupBlockedReason.SIGNUPS_CLOSED);

  return (
    <Popover
      header={t('signupSheet.signupBlockedHeader')}
      content={
        <SpaceBetween size="s">
          <ul>
            {blockedReasons.map((reason) => (
              <li key={reason}>{t(`signupSheet.blockedReasons.${SignupBlockedReason[reason]}`)}</li>
            ))}
          </ul>
          {showGivehubLink && (
            <>
              <Box>{t('signupSheet.signupBlockedFooter')}</Box>
              <Link
                href={`https://amazon.benevity.org/volunteer/${eventId}/manage/signups`}
                external
                data-aci-analytics-name={metricKey}
              >
                {t('signupSheet.manageInGivehub')}
              </Link>
            </>
          )}
        </SpaceBetween>
      }
      triggerType="custom"
      size="large"
    >
      <Icon name="status-info" />
    </Popover>
  );
}

interface SignupStatusSelectProps {
  currentValue: SignupStatus;
  onStatusChange: (status: SignupStatus) => void;
  event: Event;
  shift: Shift;
  inlineCancels?: number;
}

export const SignupStatusSelect = ({
  currentValue,
  onStatusChange,
  event,
  shift,
  inlineCancels = 0,
}: SignupStatusSelectProps) => {
  const { t } = useTranslation('signup');
  const disableReSignup =
    currentValue === SignupStatus.CANCEL &&
    getSignupBlockedReasons(event, shift, inlineCancels).length > 0;

  const STATUS_OPTIONS: SelectProps.Option[] = [
    { label: t('signupStatus.signedUp'), value: SignupStatus.SIGNUP, disabled: disableReSignup },
    { label: t('signupStatus.cancelled'), value: SignupStatus.CANCEL },
  ];

  return (
    <Select
      ariaLabel={t('signupSheet.selectStatus')}
      autoFocus
      expandToViewport
      options={STATUS_OPTIONS}
      selectedOption={STATUS_OPTIONS.find((option) => option.value === currentValue) ?? null}
      onChange={(event) => onStatusChange(event.detail.selectedOption.value as SignupStatus)}
    />
  );
};

interface DownloadSignupListButtonProps {
  event: Event;
  shift: Shift;
  signups: AmazonSignup[];
  isLoading: boolean;
  metricKey: string;
}

export function DownloadSignupListButton({
  event,
  shift,
  signups,
  isLoading,
  metricKey,
}: DownloadSignupListButtonProps) {
  const { t } = useTranslation('signup');
  const selectedTimezone = useTimezone();

  const handleDownload = () => {
    const contentTimezone = event.timeZone || selectedTimezone;
    const rows = buildSignupCsvRows(signups, shift, event, contentTimezone);
    const filename = buildSignupCsvFilename(
      event.eventId || '',
      shift.name || '',
      selectedTimezone
    );
    downloadCsv(filename, SIGNUP_CSV_HEADERS, rows);
    publishKatalMetric(metricKey, '');
  };

  return (
    <Button
      iconName="download"
      onClick={handleDownload}
      disabled={isLoading || signups.length === 0}
      data-aci-analytics-name={metricKey}
    >
      {t('signupSheet.downloadSignupList')}
    </Button>
  );
}

// Common filter properties shared between shift and event level
export const baseSignupFilterProperties: FilterProperty[] = [
  { name: 'name', operators: [':'] },
  { name: 'alias', operators: [':'] },
  { name: 'building', operators: [':'] },
  { name: 'managerLogin', operators: [':'] },
  { name: 'numberOfGuests', operators: ['=', '>', '<'] },
  { name: 'tShirtSize', operators: ['='] },
  { name: 'signupStatus', operators: ['='] },
];

export function useSignupFilterProperties(
  additionalProperties: FilterProperty[] = []
): PropertyFilterProps.FilteringProperty[] {
  const { t } = useTranslation('signup');

  const allProperties = [...baseSignupFilterProperties, ...additionalProperties];

  return allProperties.map((property) => ({
    key: property.name,
    propertyLabel: t('propertyFilter.properties.' + property.name),
    groupValuesLabel: t('propertyFilter.values.' + property.name),
    operators: property.operators,
    defaultOperator: property.operators ? property.operators[0] : undefined,
  }));
}

export function useBaseSignupColumnMap(): ColumnMap<AmazonSignup> {
  return {
    name: (item) => item.name,
    alias: (item) => (
      <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.alias}`}>
        {item.alias}
      </Link>
    ),
    building: (item) => item.building,
    managerAlias: (item) => (
      <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.managerLogin}`}>
        {item.managerLogin}
      </Link>
    ),
    numberOfGuests: (item) => String(item.numberOfGuests),
    tShirtSize: (item) => item.tShirtSize,
    signupStatus: (item) => {
      switch (item.signupStatus) {
        case SignupStatus.SIGNUP:
          return <StatusIndicator type="success">{item.signupStatus}</StatusIndicator>;
        case SignupStatus.CANCEL:
          return <StatusIndicator type="error">{item.signupStatus}</StatusIndicator>;
        case SignupStatus.PENDING:
          return <StatusIndicator type="pending">{item.signupStatus}</StatusIndicator>;
        default:
          return item.signupStatus;
      }
    },
  };
}

export function useSignupTableCollection(
  signups: AmazonSignup[],
  filteringProperties: PropertyFilterProps.FilteringProperty[],
  isError: boolean,
  pageSize: number
) {
  const { t } = useTranslation('signup');

  return useCollection(signups, {
    filtering: {
      empty: (
        <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
          <b>{isError ? t('signupSheet.errorOccurred') : t('signupSheet.noneFound')}</b>
        </Box>
      ),
    },
    propertyFiltering: {
      filteringProperties,
    },
    pagination: { pageSize },
    sorting: {},
  });
}
